import type { MouseEvent } from "react";

/**
 * z-index layer for the rich-text editor's floating UI — toolbar dropdowns
 * (font size/family), the text-color and highlight popovers, the toolbar
 * overflow menu, and the link floating toolbar.
 *
 * The editor is frequently mounted inside a MUI modal (e.g. the "New support
 * ticket" dialog), which renders at MUI's default modal z-index of 1300. These
 * overlays portal out to `<body>`, so at the components' default `z-50` they
 * paint *behind* the modal and stop receiving clicks. Lifting them just above the
 * modal layer keeps them on top in both the modal and the normal in-page editor.
 *
 * Defined once here — change the layer in a single place rather than as a magic
 * number scattered across the toolbar components. An arbitrary Tailwind value
 * (not a custom theme token) is used deliberately so `tailwind-merge` still
 * recognizes it as a z-index utility and dedupes it against each component's
 * base `z-50`.
 */
export const EDITOR_OVERLAY_Z_CLASS = "z-[1400]";

/**
 * Mouse-down handler for the color/highlight picker popovers that keeps the
 * editor's text selection alive while the user interacts with the picker.
 *
 * Clicking a popover surface normally moves DOM focus to whatever was clicked,
 * which blurs the editor and drops the selection — so by the time the picker's
 * `onChange` applies the color there is no selection to apply it to, and the
 * cursor visibly "clicks out" of the text box. Calling `preventDefault` on
 * mousedown suppresses that focus shift (the click handlers still fire), so the
 * editor keeps focus and the user can keep typing right after picking a color.
 *
 * Real form controls (the hex/RGB inputs and the format `<select>`) are exempt
 * because they need to receive focus to be usable.
 */
export function preventEditorBlurOnMouseDown(
  event: MouseEvent<HTMLElement>,
): void {
  const { target } = event;
  if (
    target instanceof HTMLElement &&
    target.closest("input, select, textarea")
  ) {
    return;
  }
  event.preventDefault();
}
